// =============================================================================
// MeuDriver — Batch O1: useVacationMode
// Hooks para ler e alternar o modo férias do instrutor autenticado.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface VacationModeData {
  vacation_mode: boolean
  vacation_start: string | null  // DATE ISO 'YYYY-MM-DD'
  vacation_end: string | null    // DATE ISO 'YYYY-MM-DD'
}

export interface ToggleVacationPayload {
  enabled: boolean
  start?: string | null
  end?: string | null
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const vacationKeys = {
  all: ['vacation-mode'] as const,
  instructor: (profileId: string) => [...vacationKeys.all, profileId] as const,
}

// ---------------------------------------------------------------------------
// useInstructorVacationMode — lê as colunas de férias do instrutor autenticado
// ---------------------------------------------------------------------------

export function useInstructorVacationMode() {
  const { user } = useAuth()

  return useQuery<VacationModeData>({
    queryKey: vacationKeys.instructor(user?.id ?? ''),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select('vacation_mode, vacation_start, vacation_end')
        .eq('profile_id', user!.id)
        .single()

      if (error) throw error

      return {
        vacation_mode: (data as any).vacation_mode ?? false,
        vacation_start: (data as any).vacation_start ?? null,
        vacation_end: (data as any).vacation_end ?? null,
      }
    },
  })
}

// ---------------------------------------------------------------------------
// useToggleVacationMode — chama RPC toggle_vacation_mode
// ---------------------------------------------------------------------------

export function useToggleVacationMode() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation<void, Error, ToggleVacationPayload>({
    mutationFn: async ({ enabled, start, end }) => {
      const { error } = await supabase.rpc('toggle_vacation_mode', {
        p_enabled: enabled,
        p_start: start ?? null,
        p_end: end ?? null,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_, variables) => {
      if (user?.id) {
        qc.invalidateQueries({ queryKey: vacationKeys.instructor(user.id) })
      }
      toast.success(
        variables.enabled
          ? 'Modo férias ativado. Novos agendamentos bloqueados.'
          : 'Modo férias desativado.',
      )
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}
